/**
 * Payment detail slice — data for the /payment page.
 *
 * - fetchPaymentDetail: POST /payments/detail via beaconApi, mapped to the
 *   PaymentDetail view model. When several legs match (reissues) the leg
 *   whose endToEndId equals the searched id is shown, else the latest.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { PaymentDetail } from './paymentDetailTypes';
import { mapPaymentDetail } from './paymentDetailMapper';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

export interface PaymentDetailState {
  data: PaymentDetail | null;
  /** Other legs returned for the same identifier (reissues). */
  otherLegIds: string[];
  loading: boolean;
  error: string | null;
  notFound: boolean;
  currentRequestId: string | null;
}

export const paymentDetailInitialState: PaymentDetailState = {
  data: null,
  otherLegIds: [],
  loading: false,
  error: null,
  notFound: false,
  currentRequestId: null,
};

interface FetchResult {
  detail: PaymentDetail | null;
  otherLegIds: string[];
}

export const fetchPaymentDetail = createAsyncThunk<
  FetchResult, string, { state: RootState; rejectValue: string }
>('paymentDetail/fetch', async (paymentId, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const response = await beaconApi.getPaymentDetail([paymentId], apiBase);
    const legs = response.endToEnds ?? [];
    if (legs.length === 0) return { detail: null, otherLegIds: [] };
    const primary = legs.find(leg => leg.endToEndId === paymentId) ?? legs[legs.length - 1];
    return {
      detail: mapPaymentDetail(primary),
      otherLegIds: legs.filter(leg => leg !== primary).map(leg => leg.endToEndId),
    };
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch payment detail');
  }
});

const paymentDetailSlice = createSlice({
  name: 'paymentDetail',
  initialState: paymentDetailInitialState,
  reducers: {
    clearPaymentDetail: () => paymentDetailInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentDetail.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.notFound = false;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchPaymentDetail.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.data = action.payload.detail;
        state.otherLegIds = action.payload.otherLegIds;
        state.notFound = action.payload.detail === null;
      })
      .addCase(fetchPaymentDetail.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch payment detail';
      });
  },
});

export const { clearPaymentDetail } = paymentDetailSlice.actions;
export default paymentDetailSlice.reducer;
